import { useEffect, useRef, useState } from 'react';
import useAvatarVoiceChat from '../hooks/useAvatarVoiceChat';
import { IS_GUEST } from '../config';
import { useVenueMingle, pushAgentLine, venueAgentSignal } from '../city/venueMingle';
import { venueAgent } from '../city/venueAgent';

// VenueAgent — THE HOST TALKS THROUGH THEIR OWN AGENT (2026-10-31).
//
// Khari: *"I believe all artists are responding in voice, but the text from
// their speech doesn't show immediately … The user types in, and the response
// is a recorded audio instead of the agent, so it takes a long time and doesn't
// appear in the log until the audio is processed."* Then: *"the better fix
// seems like putting them into agent mode, and allowing one way audio. this way
// we avoid making recordings."* All of the host's lines, members only.
//
// While a venue's host is mingling with you (VenueStage `mingling`) and you are
// signed in, this opens their voice agent ONE-WAY (useAvatarVoiceChat `oneWay`:
// no mic, ever) and tells the server (`venue_agent`: opening → on → off). Then:
//   · what you type goes to the agent (PlayerBar → city/venueAgent), which
//     answers out loud as it streams, its words in the chat at once;
//   · the server's moments (the greeting, a word about the song, a dedication,
//     a check-in, the goodbye) arrive as stage directions (`venue_dj_cue`) and
//     are handed to the agent, held while their words into the mic play;
//   · each thing the agent says goes to VenueStage as one of their lines
//     (caption, chat, the visit, the duck) and back to the server as context.
// It closes when the mingle ends or you have not moved or typed for IDLE_MS
// (the server says nothing unprompted then either), and reopens when you do.
// A refused call (no voice plan, the artist away) or one that keeps dropping
// leaves this mingle on the recorded lines, which the server goes back to the
// moment it hears `off`.
//
// Mounted by VenueStage inside the room; draws nothing.

const IDLE_MS = 5 * 60000;     // API venueMingle IDLE_STOP_MS
const CUE_WAIT_MS = 45000;     // a cue held behind the mic (or an opening call) this long at most (VenueStage LINE_WAIT_MS)
const SHORT_LIVED_MS = 10000;  // a call that closes this soon after opening counts as a failure
const MAX_FAILS = 2;

export default function VenueAgent({ roomId, a, name, mingling }) {
  const [refused, setRefused] = useState(false);
  const [idle, setIdle] = useState(false);
  const opening = useRef(false);
  const openedAt = useRef(0);
  const reported = useRef(null);       // the last state told to the server
  const fails = useRef(0);
  const held = useRef([]);             // cues waiting: { text, at }
  const who = useRef({ roomId, a, name });
  who.current = { roomId, a, name };

  const v = useAvatarVoiceChat({
    avatarId: a,
    surface: 'venue',
    venue: roomId,
    oneWay: true,
    onReply: (text) => {
      const w = who.current;
      pushAgentLine({ place: w.roomId, a: w.a, name: w.name, text });
      venueAgentSignal({ place: w.roomId, said: text });
    },
  });
  const vRef = useRef(v);
  vRef.current = v;

  const signal = (state, extra = {}) => {
    if (reported.current === state && !extra.unanswered) return;
    reported.current = state;
    venueAgentSignal({ place: roomId, state, ...extra });
  };

  // A new mingle (or a new host) is a fresh start.
  useEffect(() => { setRefused(false); fails.current = 0; held.current = []; }, [roomId, a, mingling]);

  // You have not moved or typed for a while: the call is closed until you do.
  useEffect(() => {
    const iv = setInterval(() => setIdle(Date.now() - venueAgent.activeAt > IDLE_MS), 3000);
    return () => clearInterval(iv);
  }, []);

  const want = !!(mingling && !IS_GUEST && a != null && !refused && !idle);

  /* ⚠ ORDER MATTERS: effects run in the order written. A call that closed by
     itself is cleaned up (end() drops its socket) BEFORE the open effect looks,
     or start() still sees the dead socket and quietly does nothing; and a
     refusal is handled AFTER it, or the open effect would see the attempt over
     and try again before `refused` lands. */

  // Open: tell the server (moments become cues), send what was typed meanwhile.
  // Closed by itself while still wanted (its clock ran out, it dropped): clean
  // up, and the open effect below starts a new one, unless it keeps failing.
  const wasActive = useRef(false);
  const deliberate = useRef(false);    // we closed it (the mingle ended): not a failure
  useEffect(() => {
    if (v.active) {
      wasActive.current = true;
      opening.current = false;
      venueAgent.opening = false;
      signal('on');
      for (const t of venueAgent.queue.splice(0)) v.say(t);
      return;
    }
    if (!wasActive.current) return;
    wasActive.current = false;
    if (deliberate.current) { deliberate.current = false; return; }
    if (Date.now() - openedAt.current < SHORT_LIVED_MS && ++fails.current >= MAX_FAILS) setRefused(true);
    v.end();
    signal('off');
  }, [v.active]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open while wanted, close when not.
  useEffect(() => {
    if (want && !v.active && !v.connecting && !opening.current) {
      opening.current = true;
      deliberate.current = false;
      openedAt.current = Date.now();
      venueAgent.opening = true;
      signal('opening');
      v.listen();
    } else if (!want && (v.active || v.connecting || opening.current)) {
      opening.current = false;
      deliberate.current = true;
      venueAgent.opening = false;
      v.end();
      const unanswered = venueAgent.queue.splice(0);
      signal('off', unanswered.length ? { unanswered } : {});
    }
  }, [want, v.active, v.connecting]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refused (no voice plan, the artist away, the mingle already over): the
  // recorded lines for the rest of this mingle; anything typed meanwhile goes to them.
  useEffect(() => {
    if (!v.error || !opening.current) return;
    opening.current = false;
    venueAgent.opening = false;
    console.info(`[VenueAgent] no agent for ${name || a} (${v.error}); the recorded lines carry on`);
    setRefused(true);
    v.end();
    const unanswered = venueAgent.queue.splice(0);
    signal('off', unanswered.length ? { unanswered } : {});
  }, [v.error]); // eslint-disable-line react-hooks/exhaustive-deps

  // The server's moments, for the agent to say (held while the mic is theirs).
  useEffect(() => useVenueMingle.subscribe((s, prev) => {
    const c = s.cue;
    if (!c || c === prev.cue || c.place !== roomId || Number(c.a) !== Number(who.current.a)) return;
    held.current.push({ text: c.text, at: Date.now() });
  }), [roomId]);
  useEffect(() => {
    const iv = setInterval(() => {
      const q = held.current;
      while (q.length && Date.now() - q[0].at > CUE_WAIT_MS) q.shift();
      const cur = vRef.current;
      if (!q.length || !cur.active || venueAgent.micNow || cur.speaking) return;
      cur.cue(q.shift().text);
    }, 400);
    return () => clearInterval(iv);
  }, []);

  // The shared state the stage and the player bar read.
  useEffect(() => {
    venueAgent.place = roomId;
    venueAgent.a = a ?? null;
    venueAgent.on = !!v.active;
    venueAgent.speaking = !!v.speaking;
    venueAgent.say = v.active ? (t) => vRef.current.say(t) : null;
  });

  // Leaving the room: the hook ends its own call on unmount; the server is told.
  useEffect(() => () => {
    if (reported.current && reported.current !== 'off') venueAgentSignal({ place: who.current.roomId, state: 'off' });
    Object.assign(venueAgent, { place: null, a: null, on: false, opening: false, speaking: false, say: null, queue: [] });
  }, []);

  return null;
}
